import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiDollarSign, FiChevronLeft, FiChevronRight, FiDownload, FiTrendingUp, FiTrendingDown, FiList } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, CartesianGrid } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { WO_TYPE_COLORS, WO_TYPE_LABELS } from '../utils/constants';
import { playClick } from '../utils/sounds';
import HelpTip from './HelpTip';

// Cost categories — same colours as the Dashboard cost overview. Internal
// worker labour is intentionally left out; this is spend only.
const CATS = [
  { key: 'parts', label: 'Parts', color: '#f59e0b' },
  { key: 'services', label: 'Services', color: '#0ea5e9' },
  { key: 'ext_labour', label: 'Ext. Labour', color: '#8b5cf6' },
];

const MODES = [
  { key: 'month', label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year', label: 'Year' },
  { key: 'fy', label: 'Financial Year' },
  { key: 'custom', label: 'Date Range' },
];

const GROUPS = [
  { key: 'auto', label: 'Auto' },
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const ymd = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseYmd = s => new Date(s + 'T00:00:00');
const fmtAU = s => { const d = parseYmd(s); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; };
const money = v => (v || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
const money2 = v => (v || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const moneyShort = v => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}m` : v >= 1e3 ? `$${(v / 1e3).toFixed(v >= 1e4 ? 0 : 1)}k` : `$${Math.round(v)}`;

// Range for a period mode anchored on a date
function rangeFor(mode, anchor) {
  const y = anchor.getFullYear(), m = anchor.getMonth();
  if (mode === 'month') return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
  if (mode === 'quarter') { const q = Math.floor(m / 3) * 3; return { from: ymd(new Date(y, q, 1)), to: ymd(new Date(y, q + 3, 0)) }; }
  if (mode === 'year') return { from: ymd(new Date(y, 0, 1)), to: ymd(new Date(y, 11, 31)) };
  // Australian financial year: 1 Jul – 30 Jun
  const fyStart = m >= 6 ? y : y - 1;
  return { from: ymd(new Date(fyStart, 6, 1)), to: ymd(new Date(fyStart + 1, 5, 30)) };
}

function periodLabel(mode, anchor) {
  const y = anchor.getFullYear(), m = anchor.getMonth();
  if (mode === 'month') return `${MONTHS_LONG[m]} ${y}`;
  if (mode === 'quarter') return `Q${Math.floor(m / 3) + 1} ${y}`;
  if (mode === 'year') return String(y);
  const fyStart = m >= 6 ? y : y - 1;
  return `FY ${fyStart}–${String(fyStart + 1).slice(2)}`;
}

function stepAnchor(mode, anchor, dir) {
  const d = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  if (mode === 'month') d.setMonth(d.getMonth() + dir);
  else if (mode === 'quarter') d.setMonth(d.getMonth() + dir * 3);
  else d.setFullYear(d.getFullYear() + dir);
  return d;
}

// Default bar grouping for each period mode
const DEFAULT_GROUP = { month: 'day', quarter: 'week', year: 'month', fy: 'month', custom: 'auto' };

function bucketLabel(bucket, group, long = false) {
  if (group === 'year') return bucket;
  if (group === 'month') { const [y, m] = bucket.split('-'); return long ? `${MONTHS_LONG[+m - 1]} ${y}` : `${MONTHS[+m - 1]} ${y.slice(2)}`; }
  const d = parseYmd(bucket);
  const s = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  if (group === 'week') return long ? `Week of ${s} ${d.getFullYear()}` : s;
  return long ? `${s} ${d.getFullYear()}` : s;
}

const card = { background: 'var(--bg-card)', borderRadius: 12, padding: 16, border: '1px solid var(--border)' };
const h4 = { margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' };
const pill = active => ({
  padding: '7px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
  background: active ? '#2563eb' : 'transparent', color: active ? '#fff' : 'var(--text-secondary)', whiteSpace: 'nowrap',
});
const segWrap = { display: 'flex', gap: 4, background: 'var(--bg-card)', borderRadius: 8, padding: 3, border: '1px solid var(--border)', flexWrap: 'wrap' };
const navBtn = { width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const dateInput = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13 };

export default function CostReport() {
  const { authFetch } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const axisTick = { fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' };
  const tooltipStyle = { background: isDark ? '#13213a' : '#ffffff', border: `1px solid ${isDark ? '#1f3050' : '#e2e8f0'}`, borderRadius: 8, fontSize: 12, color: isDark ? '#e8edf5' : '#1e293b', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' };
  const gridColor = isDark ? '#1e3454' : '#e2e8f0';

  const [mode, setMode] = useState('month');
  const [anchor, setAnchor] = useState(() => new Date());
  const [custom, setCustom] = useState(() => { const n = new Date(); return { from: ymd(new Date(n.getFullYear(), n.getMonth() - 2, 1)), to: ymd(n) }; });
  const [group, setGroup] = useState(DEFAULT_GROUP.month);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTable, setShowTable] = useState(false);

  const range = mode === 'custom' ? custom : rangeFor(mode, anchor);

  // authFetch changes identity when the token refreshes — keep it in a ref so
  // the report only reloads when the period or grouping actually changes.
  const authFetchRef = useRef(authFetch);
  authFetchRef.current = authFetch;

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    const g = group === 'auto' ? '' : `&group=${group}`;
    authFetchRef.current(`/api/issues/cost-report?from=${range.from}&to=${range.to}${g}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load cost report')))
      .then(d => {
        if (cancelled) return;
        setData(prev => (prev && JSON.stringify(prev) === JSON.stringify(d) ? prev : d));
        setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [range.from, range.to, group]);

  const changeMode = m => { playClick(); setMode(m); setGroup(DEFAULT_GROUP[m]); };
  const step = dir => { playClick(); setAnchor(a => stepAnchor(mode, a, dir)); };

  // Click a month/year bar to drill into it
  const drillInto = bucket => {
    if (!data) return;
    if (data.group === 'month') { const [y, m] = bucket.split('-').map(Number); setAnchor(new Date(y, m - 1, 1)); setMode('month'); setGroup('day'); playClick(); }
    else if (data.group === 'year') { setAnchor(new Date(+bucket, 0, 1)); setMode('year'); setGroup('month'); playClick(); }
  };

  const presets = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
    { label: 'Last 12 months', months: 12 },
    { label: 'Year to date', ytd: true },
  ];
  const applyPreset = p => {
    playClick();
    const n = new Date();
    let from;
    if (p.days) { from = new Date(n); from.setDate(n.getDate() - p.days + 1); }
    else if (p.months) from = new Date(n.getFullYear(), n.getMonth() - p.months + 1, 1);
    else from = new Date(n.getFullYear(), 0, 1);
    setCustom({ from: ymd(from), to: ymd(n) });
    setGroup('auto');
  };

  const t = data?.totals;
  const change = data && data.previous.total > 0 ? ((t.total - data.previous.total) / data.previous.total) * 100 : null;
  const series = useMemo(() => (data?.series || []).map(s => ({ ...s, label: bucketLabel(s.bucket, data.group) })), [data]);
  const split = t ? CATS.map(c => ({ name: c.label, value: t[c.key], color: c.color })).filter(d => d.value > 0) : [];
  const canDrill = data && (data.group === 'month' || data.group === 'year');

  const exportCsv = () => {
    if (!data) return;
    playClick();
    const esc = v => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = [
      ['Cost Report', `${fmtAU(data.from)} - ${fmtAU(data.to)}`],
      [],
      ['Period', 'Parts', 'Services', 'Ext. Labour', 'Total'],
      ...data.series.map(s => [bucketLabel(s.bucket, data.group, true), s.parts, s.services, s.ext_labour, s.total]),
      ['Total', t.parts, t.services, t.ext_labour, t.total],
      [],
      ['Work Order', 'Name', 'Type', 'Parts', 'Services', 'Ext. Labour', 'Total'],
      ...data.top_work_orders.map(w => [w.wo_number, w.name, w.type, w.parts, w.services, w.ext_labour, w.total]),
      [],
      ['Asset', 'Tag', 'Parts', 'Services', 'Ext. Labour', 'Total'],
      ...data.by_asset.map(a => [a.name, a.tag, a.parts, a.services, a.ext_labour, a.total]),
    ];
    const blob = new Blob([rows.map(r => r.map(esc).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `cost-report-${data.from}-to-${data.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const SeriesTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const s = payload[0].payload;
    return (
      <div style={{ ...tooltipStyle, padding: '10px 12px', minWidth: 170 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>{bucketLabel(s.bucket, data.group, true)}</div>
        {CATS.filter(c => s[c.key] > 0).map(c => (
          <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ color: c.color, fontWeight: 600 }}>{c.label}</span><span>{money2(s[c.key])}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderTop: `1px solid ${gridColor}`, marginTop: 6, paddingTop: 6, fontWeight: 700 }}>
          <span>Total</span><span>{money2(s.total)}</span>
        </div>
        {canDrill && s.total > 0 && <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4 }}>Click to view this {data.group}</div>}
      </div>
    );
  };

  const HBar = ({ rows, nameKey, height, onRowClick }) => (
    <ResponsiveContainer width="100%" height={height || Math.max(120, rows.length * 34 + 30)}>
      <BarChart data={rows} layout="vertical" margin={{ left: 0, right: 16 }}>
        <CartesianGrid horizontal={false} stroke={gridColor} />
        <XAxis type="number" tick={axisTick} tickFormatter={moneyShort} />
        <YAxis type="category" dataKey={nameKey} tick={axisTick} width={110} interval={0}
          tickFormatter={v => (v && v.length > 16 ? v.slice(0, 15) + '…' : v)} />
        <Tooltip cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }} contentStyle={tooltipStyle} formatter={(v, n) => [money2(v), n]} />
        {CATS.map((c, i) => <Bar key={c.key} dataKey={c.key} name={c.label} stackId="a" fill={c.color} radius={i === CATS.length - 1 ? [0, 4, 4, 0] : 0}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }} onClick={onRowClick ? (d => onRowClick(d.payload || d)) : undefined} />)}
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiDollarSign size={16} /> Cost Report
          <HelpTip text="Spend on parts, services and external (contractor) labour added to work orders, by the date they were entered. Your own workers' labour time is not included. Supervisors see their team's work orders; admins see everything." />
        </h3>
        <button onClick={exportCsv} disabled={!data || loading}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: data ? 'pointer' : 'default', fontSize: 12, fontWeight: 600, opacity: data ? 1 : 0.5 }}>
          <FiDownload size={14} /> Export CSV
        </button>
      </div>

      {/* Period controls */}
      <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
        <div style={segWrap}>
          {MODES.map(m => <button key={m.key} onClick={() => changeMode(m.key)} style={pill(mode === m.key)}>{m.label}</button>)}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {mode !== 'custom' ? (
            <>
              <button onClick={() => step(-1)} style={navBtn} title="Previous"><FiChevronLeft size={18} /></button>
              <div style={{ minWidth: 150, textAlign: 'center' }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>{periodLabel(mode, anchor)}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtAU(range.from)} – {fmtAU(range.to)}</div>
              </div>
              <button onClick={() => step(1)} style={navBtn} title="Next"><FiChevronRight size={18} /></button>
              {mode === 'month' && (
                <input type="month" value={ymd(anchor).slice(0, 7)} style={dateInput}
                  onChange={e => { if (e.target.value) { const [y, m] = e.target.value.split('-').map(Number); setAnchor(new Date(y, m - 1, 1)); } }} />
              )}
              <button onClick={() => { playClick(); setAnchor(new Date()); }}
                style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Current
              </button>
            </>
          ) : (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>
                From <input type="date" value={custom.from} onChange={e => e.target.value && setCustom(c => ({ ...c, from: e.target.value }))} style={dateInput} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>
                To <input type="date" value={custom.to} onChange={e => e.target.value && setCustom(c => ({ ...c, to: e.target.value }))} style={dateInput} />
              </label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {presets.map(p => (
                  <button key={p.label} onClick={() => applyPreset(p)}
                    style={{ padding: '6px 10px', borderRadius: 14, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                    {p.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>Show bars by</span>
          <div style={segWrap}>
            {GROUPS.map(g => <button key={g.key} onClick={() => { playClick(); setGroup(g.key); }} style={pill(group === g.key)}>{g.label}</button>)}
          </div>
          {data && group !== 'auto' && data.group !== group && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Showing by {data.group} — too many bars for this range</span>
          )}
        </div>
      </div>

      {error && <div style={{ ...card, color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {loading && !data ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ width: 32, height: 32, border: '3px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
        </div>
      ) : data && (
        <div style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s' }}>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
            <div style={{ ...card, padding: '14px 16px', gridColumn: 'span 2', background: 'linear-gradient(135deg, rgba(37,99,235,0.10), rgba(37,99,235,0.02))' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Cost</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#2563eb', lineHeight: 1.2 }}>{money(t.total)}</div>
              {change !== null ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: change > 0 ? '#dc2626' : '#16a34a', marginTop: 2 }}>
                  {change > 0 ? <FiTrendingUp size={13} /> : <FiTrendingDown size={13} />}
                  {Math.abs(change).toFixed(0)}% vs previous {mode === 'custom' ? `${data.days} days` : mode === 'fy' ? 'financial year' : mode}
                  <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>({money(data.previous.total)})</span>
                </div>
              ) : (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>No costs in the previous period to compare</div>
              )}
            </div>
            {CATS.map(c => (
              <div key={c.key} style={{ ...card, padding: '14px 16px', borderTop: `3px solid ${c.color}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{c.label}</div>
                <div style={{ fontSize: 19, fontWeight: 800, color: c.color }}>{money(t[c.key])}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.total > 0 ? `${((t[c.key] / t.total) * 100).toFixed(0)}% of total` : '—'}</div>
              </div>
            ))}
            <div style={{ ...card, padding: '14px 16px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Work Orders</div>
              <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--text-primary)' }}>{t.work_orders}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.work_orders > 0 ? `avg ${money(t.total / t.work_orders)} each` : 'with costs'}</div>
            </div>
          </div>

          {t.total === 0 ? (
            <div style={{ ...card, textAlign: 'center', padding: '36px 16px', color: 'var(--text-muted)' }}>
              <FiDollarSign size={32} style={{ opacity: 0.25, marginBottom: 8 }} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>No costs recorded in this period</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Try a different month or a wider date range</div>
            </div>
          ) : (
            <>
              {/* Cost over time */}
              <div style={{ ...card, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                  <h4 style={{ ...h4, margin: 0 }}>Cost by {data.group === 'day' ? 'Day' : data.group === 'week' ? 'Week' : data.group === 'month' ? 'Month' : 'Year'}</h4>
                  <button onClick={() => { playClick(); setShowTable(s => !s); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: showTable ? 'var(--accent-light)' : 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                    <FiList size={12} /> {showTable ? 'Hide table' : 'Show table'}
                  </button>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={series} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={gridColor} />
                    <XAxis dataKey="label" tick={axisTick} interval="preserveStartEnd" minTickGap={8} />
                    <YAxis tick={axisTick} tickFormatter={moneyShort} width={52} />
                    <Tooltip content={<SeriesTooltip />} cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {CATS.map((c, i) => (
                      <Bar key={c.key} dataKey={c.key} name={c.label} stackId="cost" fill={c.color} maxBarSize={48}
                        radius={i === CATS.length - 1 ? [4, 4, 0, 0] : 0}
                        style={{ cursor: canDrill ? 'pointer' : 'default' }}
                        onClick={canDrill ? (d => drillInto((d.payload || d).bucket)) : undefined} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>

                {showTable && (
                  <div style={{ overflowX: 'auto', marginTop: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>{['Period', ...CATS.map(c => c.label), 'Total'].map(h => (
                          <th key={h} style={{ textAlign: h === 'Period' ? 'left' : 'right', padding: '6px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {data.series.map(s => (
                          <tr key={s.bucket} style={{ opacity: s.total > 0 ? 1 : 0.5 }}>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{bucketLabel(s.bucket, data.group, true)}</td>
                            {CATS.map(c => <td key={c.key} style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', textAlign: 'right', color: 'var(--text-secondary)' }}>{s[c.key] ? money2(s[c.key]) : '—'}</td>)}
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', textAlign: 'right', fontWeight: 700, color: 'var(--text-primary)' }}>{money2(s.total)}</td>
                          </tr>
                        ))}
                        <tr>
                          <td style={{ padding: '8px', fontWeight: 800, color: 'var(--text-primary)' }}>Total</td>
                          {CATS.map(c => <td key={c.key} style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: c.color }}>{money2(t[c.key])}</td>)}
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: '#2563eb' }}>{money2(t.total)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Split + by type */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 12, marginBottom: 16 }}>
                <div style={card}>
                  <h4 style={h4}>Cost Split</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={split} cx="50%" cy="50%" outerRadius={80} innerRadius={50} dataKey="value" paddingAngle={2}>
                        {split.map(d => <Cell key={d.name} fill={d.color} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${money2(v)} (${((v / t.total) * 100).toFixed(0)}%)`, n]} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={card}>
                  <h4 style={h4}>By Work Order Type</h4>
                  <HBar rows={data.by_type.map(r => ({ ...r, label: WO_TYPE_LABELS[r.type] ? `${r.type} ${WO_TYPE_LABELS[r.type]}` : r.type }))} nameKey="label" height={220} />
                </div>
              </div>

              {/* Assets */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 12, marginBottom: 16 }}>
                {data.by_asset.length > 0 && (
                  <div style={card}>
                    <h4 style={h4}>Top Assets by Cost</h4>
                    <HBar rows={data.by_asset} nameKey="name" onRowClick={a => { if (a.id) { playClick(); navigate(`/assets/${a.id}`); } }} />
                  </div>
                )}
              </div>

              {/* Top work orders */}
              {data.top_work_orders.length > 0 && (
                <div style={card}>
                  <h4 style={h4}>Costliest Work Orders</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {data.top_work_orders.map(wo => (
                      <div key={wo.id} onClick={() => { playClick(); navigate(`/work-orders/${wo.wo_number}`); }}
                        style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'monospace', color: WO_TYPE_COLORS[wo.type] || '#2563eb' }}>{wo.wo_number}</span>
                          <span style={{ padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[wo.type] || '#6b7280' }}>{wo.type}</span>
                          <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 100 }}>{wo.name}</span>
                          <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>{money(wo.total)}</span>
                        </div>
                        {/* Proportional bar showing what the WO's cost is made of */}
                        <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: 'var(--border)' }}>
                          {CATS.filter(c => wo[c.key] > 0).map(c => (
                            <div key={c.key} title={`${c.label}: ${money2(wo[c.key])}`} style={{ width: `${(wo[c.key] / wo.total) * 100}%`, background: c.color }} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
